/**
 * Client-side helper for consuming the Copilot streaming (Server-Sent Events) endpoints.
 *
 * We use `fetch` + `ReadableStream` (not the native `EventSource`) because the request is a
 * POST that carries an Authorization header — neither of which `EventSource` supports.
 * The server sends one JSON object per SSE `data:` line, terminated by a blank line.
 */

import { IPublicClientApplication } from "@azure/msal-browser";
import { fetchCopilotResponse } from "./copilotRequest";

export interface AgentStreamStepDto {
  callId: string;
  name: string;
  status: "running" | "success" | "error";
  source?: string;
  arguments?: { name: string; value: string }[];
  content?: string;
  isError?: boolean;
}

export interface AgentStreamEventDto {
  type: "meta" | "token" | "step" | "thought" | "done" | "error";
  sessionId?: string;
  token?: string;
  source?: string;
  step?: AgentStreamStepDto;
  // Authoritative messages on `done` (shape matches the non-streaming API response).
  messages?: any[];
  error?: string;
}

export interface CopilotStreamHandlers {
  onMeta?: (sessionId: string) => void;
  onToken?: (token: string) => void;
  onStep?: (step: AgentStreamStepDto) => void;
  onThought?: (thought: string, source?: string) => void;
  onDone?: (messages: any[], sessionId: string) => void;
  onError?: (error: string) => void;
}

const dispatch = (evt: AgentStreamEventDto, handlers: CopilotStreamHandlers): void => {
  switch (evt.type) {
    case "meta":
      handlers.onMeta?.(evt.sessionId ?? "");
      break;
    case "token":
      if (evt.token) handlers.onToken?.(evt.token);
      break;
    case "step":
      if (evt.step) handlers.onStep?.(evt.step);
      break;
    case "thought":
      if (evt.token) handlers.onThought?.(evt.token, evt.source);
      break;
    case "done":
      handlers.onDone?.(evt.messages ?? [], evt.sessionId ?? "");
      break;
    case "error":
      handlers.onError?.(evt.error ?? "stream_error");
      break;
    default:
      break;
  }
};

/**
 * POSTs to a streaming Copilot endpoint and invokes the handlers as SSE events arrive.
 * Resolves when the stream ends (`done`/`error`) or the reader completes.
 * Aborting via `signal` rejects with an `AbortError`, which the caller should catch.
 */
export async function streamCopilotResponse(
  instance: IPublicClientApplication,
  url: string,
  body: unknown,
  handlers: CopilotStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetchCopilotResponse(instance, url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(body),
    signal
  });

  if (!response.ok || !response.body) {
    handlers.onError?.(`http_${response.status}`);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    let newlineIndex: number;
    while ((newlineIndex = buffer.indexOf("\n")) >= 0) {
      const line = buffer.slice(0, newlineIndex).trim();
      buffer = buffer.slice(newlineIndex + 1);

      if (!line.startsWith("data:")) continue;
      const json = line.slice("data:".length).trim();
      if (!json) continue;

      let evt: AgentStreamEventDto;
      try {
        evt = JSON.parse(json);
      } catch {
        continue; // ignore malformed frames
      }

      dispatch(evt, handlers);
      if (evt.type === "done" || evt.type === "error") {
        return;
      }
    }
  }
}
